import React, { useState, useEffect } from 'react';
import { getStudentData } from '../assets/data';
import './MaterialsSelectorModal.css';

const MaterialsSelectorModal = ({ isOpen, onClose, onSelectMaterial, activeEditor, setPdfModal }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [allMaterials, setAllMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [previewMaterial, setPreviewMaterial] = useState(null);

  // Загружаем все материалы из всех студентов
  useEffect(() => {
    if (isOpen) {
      const students = getStudentData();
      const materials = [];
      
      Object.entries(students).forEach(([studentId, student]) => {
        if (student.materials && Array.isArray(student.materials)) {
          student.materials.forEach(material => {
            materials.push({
              ...material,
              studentId,
              studentName: student.name
            });
          });
        }
      });
      
      setAllMaterials(materials);
    }
  }, [isOpen]);

  // Фильтруем материалы по поиску и студенту
  useEffect(() => {
    let filtered = allMaterials;
    
    // Фильтр по студенту
    if (selectedStudent !== 'all') {
      filtered = filtered.filter(material => material.studentId === selectedStudent);
    }
    
    // Фильтр по поиску
    if (searchTerm.trim()) {
      filtered = filtered.filter(material => 
        material.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredMaterials(filtered);
  }, [allMaterials, searchTerm, selectedStudent]);

  const handleMaterialPreview = (material) => {
    // Для ссылок открываем сразу в новой вкладке
    if (material.type === 'link') {
      window.open(material.value, '_blank', 'noopener,noreferrer');
      return;
    }
    setPreviewMaterial(material);
  };

  const handleClosePreview = () => {
    setPreviewMaterial(null);
  };

  // Функция для определения YouTube ссылок
  const isYouTubeLink = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  // Функция для извлечения YouTube ID
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // eslint-disable-next-line no-unused-vars
  const handleMaterialSelect = (material) => {
    if (material.type === 'file') {
      const extension = material.extension || material.name.split('.').pop().toLowerCase();
      
      if (extension === 'pdf') {
        // Открываем модальное окно выбора страниц PDF
        setPdfModal({ 
          url: material.value, 
          filename: material.name, 
          activeEditor 
        });
      } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
        // Вставляем изображение
        activeEditor.chain().focus().setImage({
          src: material.value,
          width: '400px',
          height: 'auto',
          align: 'center',
          draggable: true,
          class: 'resizable-image'
        }).run();
      } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        // Вставляем видео
        activeEditor.chain().focus().insertContent({
          type: 'video',
          attrs: {
            src: material.value,
            controls: true,
            width: '400px',
            height: 'auto'
          }
        }).run();
      } else if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
        // Вставляем аудио
        activeEditor.chain().focus().insertContent({
          type: 'audioNode',
          attrs: {
            src: material.value,
            controls: true,
            width: '400px',
            height: 'auto'
          }
        }).run();
      } else {
        // Вставляем файл напрямую
        activeEditor.chain().focus().insertContent({
          type: 'file',
          attrs: {
            src: material.value,
            filename: material.name,
            size: material.file?.size || 0,
            type: material.file?.type || 'application/octet-stream'
          }
        }).run();
      }
    } else if (material.type === 'link') {
      // Проверяем, является ли ссылка YouTube видео
      if (isYouTubeLink(material.value)) {
        const videoId = getYouTubeId(material.value);
        if (videoId) {
          // Вставляем YouTube видео
          activeEditor.chain().focus().insertContent({
            type: 'youtube',
            attrs: {
              src: `https://www.youtube.com/embed/${videoId}`,
              width: '400px',
              height: '300px'
            }
          }).run();
        } else {
          // Если не удалось извлечь ID, вставляем как обычную ссылку
          activeEditor.chain().focus().setLink({ href: material.value }).run();
        }
      } else {
        // Вставляем обычную ссылку
        activeEditor.chain().focus().setLink({ href: material.value }).run();
      }
    }
    
    onClose();
  };

  const getFileIcon = (material) => {
    if (material.type === 'link') {
      return (
        <svg width="12" height="12" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
          <path d="m138.433594 198.664062-102.796875 102.773438c-47.515625 47.507812-47.515625 124.804688 0 172.3125l2.566406 2.566406c23.019531 23.007813 53.621094 35.683594 86.175781 35.683594 32.554688 0 63.160156-12.675781 86.175782-35.683594l98.910156-98.886718c-7.246094 1.046874-14.613282 1.585937-22.066406 1.585937-20.503907 0-40.382813-4.039063-58.734376-11.734375l-62.300781 62.285156c-11.214843 11.210938-26.125 17.386719-41.984375 17.386719-15.855468 0-30.765625-6.171875-41.980468-17.382813-11.199219-11.195312-17.367188-26.101562-17.367188-41.976562 0-15.871094 6.167969-30.78125 17.363281-41.976562l100.230469-100.203126c11.214844-11.214843 26.125-17.386718 41.984375-17.386718 15.335937 0 29.773437 5.785156 40.851563 16.304687.402343.328125.789062.675781 1.164062 1.050781 5.550781 5.546876 12.925781 8.601563 20.773438 8.601563 7.84375 0 15.222656-3.054687 20.769531-8.601563l22.441406-22.433593c-4.886719-7.835938-10.65625-15.121094-17.257813-21.71875l-2.5625-2.5625c-23.019531-23.015625-53.625-35.6875-86.175781-35.6875-32.554687 0-63.160156 12.671875-86.179687 35.683593zm0 0" fill="#ee5a24"/>
          <path d="m476.367188 210.5625c22.980468-22.96875 35.632812-53.566406 35.632812-86.15625 0-32.585938-12.65625-63.183594-35.632812-86.15625l-2.5625-2.5625c-23.019532-23.011719-53.625-35.6875-86.179688-35.6875-32.550781 0-63.15625 12.675781-86.175781 35.6875l-98.910157 98.882812c7.246094-1.046874 14.617188-1.585937 22.070313-1.585937 20.5 0 40.378906 4.042969 58.730469 11.734375l62.300781-62.285156c11.214844-11.210938 26.125-17.386719 41.984375-17.386719s30.769531 6.171875 41.984375 17.386719c11.199219 11.195312 17.363281 26.101562 17.363281 41.972656 0 15.875-6.164062 30.78125-17.363281 41.972656l-100.230469 100.207032c-11.214844 11.214843-26.125 17.386718-41.984375 17.386718-15.332031 0-29.769531-5.785156-40.847656-16.304687-.402344-.324219-.792969-.675781-1.167969-1.050781-5.546875-5.546876-12.925781-8.601563-20.769531-8.601563-7.847656 0-15.222656 3.054687-20.769531 8.601563l-22.441406 22.4375c4.882812 7.832031 10.652343 15.113281 17.253906 21.714843l2.566406 2.566407c23.019531 23.011718 53.621094 35.683593 86.175781 35.683593 32.554688 0 63.160157-12.671875 86.175781-35.683593zm0 0" fill="#ee5a24"/>
        </svg>
      );
    }
    
    const extension = material.extension || material.name.split('.').pop().toLowerCase();
    
    if (extension === 'pdf') {
      return (
        <svg width="16" height="16" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(22 4.998)">
            <path d="m87-4.9980469c-33.051062 0-60 26.9489439-60 59.9999999v391.999997c0 33.05087 26.94887 60 60 60h294c33.05093 0 60-26.94906 60-60v-294.0039c0-18.59674-7.39309-36.44387-20.54297-49.59375l-87.85937-87.859378c-13.1499-13.1498723-30.99506-20.5429733-49.5918-20.5429689zm48.99414 146.9980469h98a10.9995 10.9995 0 0 1 11 11 10.9995 10.9995 0 0 1 -11 10.99805l-98 .002a10.9995 10.9995 0 0 1 -11-11 10.9995 10.9995 0 0 1 11-11zm0 98h196a11.00005 11.00005 0 0 1 11 11 11.00005 11.00005 0 0 1 -11 11h-196a11.00005 11.00005 0 0 1 -11-11 11.00005 11.00005 0 0 1 11-11zm.004 97.99609h195.99804a11.00005 11.00005 0 0 1 11.00196 11 11.00005 11.00005 0 0 1 -11.00196 11h-195.99813a11.00005 11.00005 0 0 1 -11.00196-11 11.00005 11.00005 0 0 1 11.00196-11z" clipRule="evenodd" fillRule="evenodd" strokeWidth="22" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" fill="#ee5a24" stroke="#ee5a24"/>
            <path d="m135.99414 141.9980469h98a10.9995 10.9995 0 0 1 11 11 10.9995 10.9995 0 0 1 -11 10.99805l-98 .002a10.9995 10.9995 0 0 1 -11-11 10.9995 10.9995 0 0 1 11-11zm0 98h196a11.00005 11.00005 0 0 1 11 11 11.00005 11.00005 0 0 1 -11 11h-196a11.00005 11.00005 0 0 1 -11-11 11.00005 11.00005 0 0 1 11-11zm.004 97.99609h195.99804a11.00005 11.00005 0 0 1 11.00196 11 11.00005 11.00005 0 0 1 -11.00196 11h-195.99813a11.00005 11.00005 0 0 1 -11.00196-11 11.00005 11.00005 0 0 1 11.00196-11z" fill="white"/>
          </g>
        </svg>
      );
    }
    
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
      return (
        <svg width="16" height="16" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(22 -5.002)">
            <path d="m87 5.0019531c-33.051057 0-60 26.9489429-60 59.9999999v391.999997c0 33.05087 26.948875 60 60 60h294c33.05113 0 60-26.94913 60-60v-294.0039c.00001-18.59674-7.39309-36.44387-20.54297-49.59375l-87.85937-87.859378c-13.1499-13.149868-30.99507-20.5429708-49.5918-20.5429689zm171.49023 171.4999969c18.8388-.00061 34.30991 15.02645 35.31641 33.63282l13.08008-13.08399c6.33207-6.33433 15.81761-7.58586 23.05273-4.58984 7.23513 2.996 13.06044 10.58839 13.06055 19.54492v97.97852c.00014 8.95722-5.82512 16.54783-13.06055 19.54492-7.23541 2.99708-16.72088 1.74763-23.05468-4.58594l-13.07422-13.07422c-1.0024 18.60473-16.47207 33.63294-35.3086 33.63281h-98.0039c-19.47443.00008-35.49793-16.02364-35.49805-35.49804v-24.49805a11.00005 11.00005 0 0 1 11-11 11.00005 11.00005 0 0 1 11 11v24.49805c.00005 7.58455 5.91315 13.49807 13.49805 13.49804h98.0039c7.58489.00005 13.49802-5.91349 13.49805-13.49804v-24.49805a11.00115 11.00115 0 0 1 18.77734-7.7793l30.22266 30.22071v-93.89844l-30.2207 30.23437a11.00115 11.00115 0 0 1 -18.7793-7.77734l-.002-24.5c-.00081-7.5889-5.91858-13.50415-13.50782-13.50391l-97.98632.004c-7.58892.00027-13.50395 5.91531-13.50391 13.50391v24.49804a11.00005 11.00005 0 0 1 -11 11 11.00005 11.00005 0 0 1 -11-11v-24.49804c-.00009-19.47821 16.02568-35.50517 35.50391-35.50586z" clipRule="evenodd" fillRule="evenodd" strokeWidth="22" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" fill="#ee5a24" stroke="#ee5a24"/>
            <path d="m258.49023 176.4999969c18.8388-.00061 34.30991 15.02645 35.31641 33.63282l13.08008-13.08399c6.33207-6.33433 15.81761-7.58586 23.05273-4.58984 7.23513 2.996 13.06044 10.58839 13.06055 19.54492v97.97852c.00014 8.95722-5.82512 16.54783-13.06055 19.54492-7.23541 2.99708-16.72088 1.74763-23.05468-4.58594l-13.07422-13.07422c-1.0024 18.60473-16.47207 33.63294-35.3086 33.63281h-98.0039c-19.47443.00008-35.49793-16.02364-35.49805-35.49804v-24.49805a11.00005 11.00005 0 0 1 11-11 11.00005 11.00005 0 0 1 11 11v24.49805c.00005 7.58455 5.91315 13.49807 13.49805 13.49804h98.0039c7.58489.00005 13.49802-5.91349 13.49805-13.49804v-24.49805a11.00115 11.00115 0 0 1 18.77734-7.7793l30.22266 30.22071v-93.89844l-30.2207 30.23437a11.00115 11.00115 0 0 1 -18.7793-7.77734l-.002-24.5c-.00081-7.5889-5.91858-13.50415-13.50782-13.50391l-97.98632.004c-7.58892.00027-13.50395 5.91531-13.50391 13.50391v24.49804a11.00005 11.00005 0 0 1 -11 11 11.00005 11.00005 0 0 1 -11-11v-24.49804c-.00009-19.47821 16.02568-35.50517 35.50391-35.50586z" fill="white"/>
          </g>
        </svg>
      );
    }
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="#ee5a24" stroke="#ee5a24" strokeWidth="2"/>
          <circle cx="8.5" cy="8.5" r="1.5" fill="white"/>
          <polygon points="21,15 16,10 5,21" fill="white"/>
        </svg>
      );
    }
    
    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
      return (
        <svg width="12" height="12" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
          <g id="Layer_2" data-name="Layer 2">
            <g id="Layer_1_copy_4" data-name="Layer 1 copy 4">
              <g id="_48" data-name="48">
                <path d="m302.05 147.42a11.12 11.12 0 0 0 -11.46.32l-99.24 62.41-1 .55v97.7l100.75 56.18a11.29 11.29 0 0 0 16.78-9.86v-197.42a11.15 11.15 0 0 0 -5.83-9.88z" fill="#ee5a24"/>
                <path d="m413.89 0h-315.78a98.1 98.1 0 0 0 -98.11 98.11v315.78a98.1 98.1 0 0 0 98.11 98.11h315.78a98.1 98.1 0 0 0 98.11-98.11v-315.78a98.1 98.1 0 0 0 -98.11-98.11zm-89.89 354.72a27.35 27.35 0 0 1 -40.76 23.93l-101.35-56.54a11.39 11.39 0 0 0 -5.5-1.43h-58a27.43 27.43 0 0 1 -27.39-27.4v-67.64a27.44 27.44 0 0 1 27.4-27.41h58.36a11.32 11.32 0 0 0 6-1.73l99.24-62.4a27.4 27.4 0 0 1 42 23.2zm30.33-34a8.06 8.06 0 0 1 -6.48-12.84c16.54-22.46 21.73-45.45 15.42-68.32a75.9 75.9 0 0 0 -14.85-28.56 8.06 8.06 0 0 1 11.82-11 91 91 0 0 1 18.36 34.4c5.51 19.34 6.91 49.43-17.77 82.94a8 8 0 0 1 -6.5 3.34zm64.89-40.81c-4 25.19-15.06 50.13-32.73 74.14a8.06 8.06 0 1 1 -13-9.56c29-39.41 38-79.87 26.72-120.24-8.51-30.44-26-49.59-26.14-49.78a8.06 8.06 0 0 1 11.81-11c.81.87 20 21.71 29.64 55.67a139.12 139.12 0 0 1 3.7 60.73z" fill="#ee5a24"/>
                <path d="m107.12 225.64v67.64a11.29 11.29 0 0 0 11.28 11.28h55.87v-90.21h-55.87a11.3 11.3 0 0 0 -11.28 11.29z" fill="#ee5a24"/>
              </g>
            </g>
          </g>
        </svg>
      );
    }
    
    // Иконка по умолчанию для других файлов
    return (
      <svg width="16" height="16" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(22 4.998)">
          <path d="m87-4.9980469c-33.051062 0-60 26.9489439-60 59.9999999v391.999997c0 33.05087 26.94887 60 60 60h294c33.05093 0 60-26.94906 60-60v-294.0039c0-18.59674-7.39309-36.44387-20.54297-49.59375l-87.85937-87.859378c-13.1499-13.1498723-30.99506-20.5429733-49.5918-20.5429689zm48.99414 146.9980469h98a10.9995 10.9995 0 0 1 11 11 10.9995 10.9995 0 0 1 -11 10.99805l-98 .002a10.9995 10.9995 0 0 1 -11-11 10.9995 10.9995 0 0 1 11-11zm0 98h196a11.00005 11.00005 0 0 1 11 11 11.00005 11.00005 0 0 1 -11 11h-196a11.00005 11.00005 0 0 1 -11-11 11.00005 11.00005 0 0 1 11-11zm.004 97.99609h195.99804a11.00005 11.00005 0 0 1 11.00196 11 11.00005 11.00005 0 0 1 -11.00196 11h-195.99813a11.00005 11.00005 0 0 1 -11.00196-11 11.00005 11.00005 0 0 1 11.00196-11z" clipRule="evenodd" fillRule="evenodd" strokeWidth="22" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10"/>
        </g>
      </svg>
    );
  };

  const students = getStudentData();
  const studentOptions = Object.entries(students).map(([id, student]) => ({
    id,
    name: student.name
  }));

  if (!isOpen) return null;

  return (
    <div className="materials-selector-modal-overlay" onClick={onClose}>
      <div className="materials-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="materials-selector-header">
          <h2>Select Material</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        
        <div className="materials-selector-filters">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search materials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="student-filter">
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="student-select"
            >
              <option value="all">All Students</option>
              {studentOptions.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="materials-list">
          {filteredMaterials.length === 0 ? (
            <div className="no-materials">
              <p>No materials found</p>
            </div>
          ) : (
            filteredMaterials.map((material, index) => (
              <div
                key={`${material.studentId}-${index}`}
                className="material-item"
                onClick={() => onSelectMaterial(material)}
              >
                <div className="material-icon">
                  {getFileIcon(material)}
                </div>
                <div className="material-info">
                  <div className="material-name">{material.name}</div>
                  <div className="material-meta">
                    <span className="student-name">{material.studentName}</span>
                  </div>
                </div>
                <div className="material-actions">
                  <button 
                    className="preview-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMaterialPreview(material);
                    }}
                    title="Preview"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Preview Modal */}
      {previewMaterial && (
        <div className="materials-preview-modal-overlay" onClick={handleClosePreview}>
          <div className="materials-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="materials-preview-header">
              <h3>{previewMaterial.name}</h3>
              <button className="close-button" onClick={handleClosePreview}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            <div className="materials-preview-content">
              {previewMaterial.type === 'link' ? (
                <div className="link-preview">
                  <p>Link: <a href={previewMaterial.value} target="_blank" rel="noopener noreferrer">{previewMaterial.value}</a></p>
                </div>
              ) : (
                (() => {
                  const extension = previewMaterial.extension || previewMaterial.name.split('.').pop().toLowerCase();
                  
                  if (extension === 'pdf') {
                    return (
                      <div className="pdf-preview">
                        <iframe 
                          src={previewMaterial.value + '#toolbar=0&navpanes=0&scrollbar=0'} 
                          width="100%" 
                          height="400px" 
                          title="PDF Preview"
                          frameBorder="0"
                        />
                      </div>
                    );
                  } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
                    return (
                      <div className="video-preview">
                        <video controls width="100%" height="300px">
                          <source src={previewMaterial.value} type="video/mp4" />
                          <source src={previewMaterial.value} type="video/webm" />
                          <source src={previewMaterial.value} type="video/ogg" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    );
                  } else if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
                    return (
                      <div className="audio-preview">
                        <audio controls style={{ width: '100%' }}>
                          <source src={previewMaterial.value} type="audio/mpeg" />
                          <source src={previewMaterial.value} type="audio/wav" />
                          <source src={previewMaterial.value} type="audio/ogg" />
                          Your browser does not support the audio tag.
                        </audio>
                      </div>
                    );
                  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
                    return (
                      <div className="image-preview">
                        <img 
                          src={previewMaterial.value} 
                          alt={previewMaterial.name}
                          style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div className="file-preview">
                        <p>File: {previewMaterial.name}</p>
                        <p>Type: {extension.toUpperCase()}</p>
                        <p>Size: {previewMaterial.file?.size ? `${(previewMaterial.file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}</p>
                      </div>
                    );
                  }
                })()
              )}
            </div>
            
            <div className="materials-preview-footer">
              <button className="insert-material-btn" onClick={() => onSelectMaterial(previewMaterial)}>
                Insert Material
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsSelectorModal; 